/**
 * Resume Service
 *
 * Frontend service for resume management and achievement extraction
 */

import { getFunctions } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { getEmulatorHost, isUsingEmulators } from './emulator';

export interface ResumeExperience {
  company: string;
  position: string;
  location?: string;
  startDate: string;
  endDate?: string;
  achievements: string[];
  skills?: string[];
}

export interface ResumeEducation {
  institution: string;
  degree: string;
  field: string;
  location?: string;
  startDate?: string;
  endDate?: string;
  graduationDate?: string;
  isCurrentlyAttending?: boolean;
  description?: string;
  gpa?: string;
  honors?: string[];
}

export interface ResumeProject {
  name: string;
  description: string;
  technologies: string[];
  achievements: string[];
  url?: string;
  startDate?: string;
  endDate?: string;
}

export type SkillProficiency = 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';

export interface ResumeSkillEntry {
  name: string;
  proficiency: SkillProficiency;
}

export interface ResumeSkills {
  technical: string[];
  soft: string[];
  technicalEntries?: ResumeSkillEntry[];
  softEntries?: ResumeSkillEntry[];
  languages?: string[];
  certifications?: string[]; // legacy; prefer ResumeData.certifications
}

export interface Certification {
  name: string;
  issuer?: string;
  issuedDate?: string;
  expiryDate?: string;
  credentialId?: string;
  url?: string;
}

export interface ResumeData {
  personalInfo: {
    name: string;
    email: string;
    phone?: string;
    location?: string;
    linkedin?: string;
    github?: string;
    website?: string;
  };
  summary?: string;
  experience: ResumeExperience[];
  education: ResumeEducation[];
  skills: ResumeSkills;
  projects?: ResumeProject[];
  certifications?: Certification[];
  awards?: string[];
  publications?: string[];
}

export interface Resume {
  id: string;
  userId: string;
  data: ResumeData;
  version: number;
  createdAt: string;
  updatedAt: string;
  lastExtractedFrom?: string;
}

export interface ResumeVersionSummary {
  id: string;
  version: number;
  resumeId?: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface Achievement {
  text: string;
  category: 'experience' | 'project' | 'skill' | 'award';
  suggestedCompany?: string;
  suggestedPosition?: string;
  sourceConversationId?: string;
  sourceConversationTitle?: string;
  confidence: number;
}

export interface AchievementExtraction {
  conversationId: string;
  achievements: Achievement[];
  model?: string;
  message?: string;
}

export interface ResumeCritiqueSuggestion {
  id: string;
  description: string;
  reason: string;
  proposedChange: string;
  sectionAffected: 'summary' | 'experience' | 'skills' | 'education' | 'projects' | 'achievements';
  priority?: 'high' | 'medium' | 'low';
  impact?: string;
}

export interface ResumeSectionScore {
  score: number;
  status: 'excellent' | 'good' | 'needs_work' | 'missing';
  comment: string;
}

export interface ResumeScorecard {
  overall: number;
  sections: {
    summary: ResumeSectionScore;
    experience: ResumeSectionScore;
    skills: ResumeSectionScore;
    ats_readiness: ResumeSectionScore;
  };
}

export interface ResumeCritiqueResult {
  suggestions: ResumeCritiqueSuggestion[];
  scorecard?: ResumeScorecard;
  model?: string;
  message?: string;
}

export type LocationStyle = 'keep' | 'us' | 'international';

export interface NormalizeResumeSectionsResult {
  normalized: {
    experience?: ResumeExperience[];
    education?: ResumeEducation[];
  };
  locationStyle: LocationStyle;
}

export interface ImportResumeFromPdfResult {
  resumeData: ResumeData;
  model?: string;
  usage?: any;
}

export async function importResumeFromPdf(
  file: File,
  options?: { language?: string }
): Promise<ImportResumeFromPdfResult> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  if (!file) {
    throw new Error('PDF file is required');
  }

  if (file.type && file.type !== 'application/pdf') {
    throw new Error('Only PDF files are supported');
  }

  const idToken = await user.getIdToken(isUsingEmulators());

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  const controller = new AbortController();
  // PDF import can be slower than typical JSON endpoints, especially when using a local LLM.
  // If a global API timeout is configured lower (e.g. 60s), keep a higher minimum for this endpoint.
  const apiTimeoutMs = parseInt(process.env.REACT_APP_API_TIMEOUT || '60000', 10);
  const importTimeoutOverrideMs = process.env.REACT_APP_PDF_IMPORT_TIMEOUT_MS
    ? parseInt(process.env.REACT_APP_PDF_IMPORT_TIMEOUT_MS, 10)
    : NaN;
  const timeoutMs = Number.isFinite(importTimeoutOverrideMs)
    ? importTimeoutOverrideMs
    : Math.max(Number.isFinite(apiTimeoutMs) ? apiTimeoutMs : 60000, 180000);
  const timeoutId = setTimeout(
    () => controller.abort(),
    Number.isFinite(timeoutMs) ? timeoutMs : 180000
  );

  try {
    const body = await file.arrayBuffer();
    const response = await fetch(`${baseUrl}/importResumeFromPdf`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/pdf',
        ...(options?.language ? { 'X-Postmortem-Language': options.language } : {}),
      },
      body,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      // Log raw LLM response if available (for debugging JSON parsing errors)
      if (data?.raw) {
        console.error('[Resume API] Raw LLM response that failed to parse:', data.raw);
      }
      throw new Error(data?.error || `HTTP error! status: ${response.status}`);
    }

    if (!data?.resumeData) {
      throw new Error('Invalid import response');
    }

    return data as ImportResumeFromPdfResult;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout - PDF import is taking too long. If using a local LLM, this can take 1–3 minutes.');
    }
    throw error;
  }
}

/**
 * Fetch the user's current resume
 */
export async function getResume(): Promise<Resume | null> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(isUsingEmulators());

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  // Create abort controller for fetch timeout
  const controller = new AbortController();
  const timeoutMs = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
  const timeoutId = setTimeout(() => controller.abort(), Number.isFinite(timeoutMs) ? timeoutMs : 30000);

  try {
    console.log(`[Resume API] Fetching resume from ${baseUrl}/getResume`);
    
    const response = await fetch(`${baseUrl}/getResume`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[Resume API] HTTP error ${response.status}: ${errorText}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('[Resume API] Fetch successful:', data);

    const resume: Resume | null = data.resume;
    // Guard: LLMs occasionally return summary as a structured object instead of a string.
    // Coerce it so the Resume component can render it safely.
    if (resume?.data?.summary && typeof resume.data.summary !== 'string') {
      const raw = resume.data.summary as any;
      resume.data.summary = typeof raw?.text === 'string' ? raw.text : JSON.stringify(raw);
    }
    return resume;
  } catch (error: any) {
    clearTimeout(timeoutId);
    console.error('[Resume API] Error:', error);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - resume service not responding');
    }
    throw error;
  }
}

/**
 * Update the user's resume
 */
export async function updateResume(resumeData: ResumeData): Promise<Resume> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(isUsingEmulators());

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutMs = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
  const timeoutId = setTimeout(() => controller.abort(), Number.isFinite(timeoutMs) ? timeoutMs : 30000);

  try {
    console.log(`[Resume API] Updating resume at ${baseUrl}/updateResume`);
    
    const response = await fetch(`${baseUrl}/updateResume`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: resumeData }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[Resume API] HTTP error ${response.status}: ${errorText}`);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log(`[Resume API] Update successful:`, result);
    
    if (!result.resume) {
      throw new Error('No resume data in response');
    }
    
    return result.resume;
  } catch (error: any) {
    clearTimeout(timeoutId);
    console.error('[Resume API] Error:', error);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - resume service not responding');
    }
    throw error;
  }
}

/**
 * Extract achievements from a conversation
 */
export async function extractConversationAchievements(conversationId?: string): Promise<AchievementExtraction> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(isUsingEmulators());

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutMs = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
  const timeoutId = setTimeout(() => controller.abort(), Number.isFinite(timeoutMs) ? timeoutMs : 30000);

  try {
    const response = await fetch(`${baseUrl}/extractConversationAchievements`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...(conversationId ? { conversationId } : {}) }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.extraction;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - achievement extraction service not responding');
    }
    throw error;
  }
}

export async function normalizeResumeSections(params: {
  experience?: ResumeExperience[];
  education?: ResumeEducation[];
  locationStyle?: LocationStyle;
}): Promise<NormalizeResumeSectionsResult> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(isUsingEmulators());

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  const controller = new AbortController();
  const timeoutMs = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
  const timeoutId = setTimeout(() => controller.abort(), Number.isFinite(timeoutMs) ? timeoutMs : 30000);

  try {
    const response = await fetch(`${baseUrl}/normalizeResumeSections`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        experience: params.experience,
        education: params.education,
        locationStyle: params.locationStyle || 'keep',
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result = (await response.json()) as NormalizeResumeSectionsResult;
    return result;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - normalization service not responding');
    }
    throw error;
  }
}

export async function getResumeVersions(): Promise<ResumeVersionSummary[]> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(isUsingEmulators());

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  const controller = new AbortController();
  const timeoutMs = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
  const timeoutId = setTimeout(() => controller.abort(), Number.isFinite(timeoutMs) ? timeoutMs : 30000);

  try {
    const response = await fetch(`${baseUrl}/getResumeVersions`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return Array.isArray(result?.versions) ? result.versions : [];
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - resume history service not responding');
    }
    throw error;
  }
}

export async function getResumeVersion(version: number): Promise<Resume> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(isUsingEmulators());

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  const controller = new AbortController();
  const timeoutMs = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
  const timeoutId = setTimeout(() => controller.abort(), Number.isFinite(timeoutMs) ? timeoutMs : 30000);

  try {
    const response = await fetch(`${baseUrl}/getResumeVersion?version=${version}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    if (!result?.resume) {
      throw new Error('No resume data in response');
    }
    return result.resume as Resume;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - resume history service not responding');
    }
    throw error;
  }
}

export async function critiqueResume(maxSuggestions: number = 5): Promise<ResumeCritiqueResult> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(isUsingEmulators());

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  const controller = new AbortController();
  const timeoutMs = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
  const timeoutId = setTimeout(() => controller.abort(), Number.isFinite(timeoutMs) ? timeoutMs : 30000);

  try {
    const response = await fetch(`${baseUrl}/critiqueResume`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ maxSuggestions }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    const critique = result?.critique;
    return {
      suggestions: Array.isArray(critique?.suggestions) ? critique.suggestions : [],
      scorecard: critique?.scorecard,
      model: critique?.model,
      message: critique?.message,
    };
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - resume critique service not responding');
    }
    throw error;
  }
}

export interface RefineSuggestionParams {
  description: string;
  sectionAffected: string;
  currentProposedChange: string;
  userFeedback: string;
}

export async function refineCritiqueSuggestion(params: RefineSuggestionParams): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Must be authenticated');

  const idToken = await user.getIdToken(isUsingEmulators());

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch(`${baseUrl}/refineCritiqueSuggestion`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    if (!response.ok) {
      const err = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${err}`);
    }
    const result = await response.json();
    return result.proposedChange as string;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') throw new Error('Request timeout — refine service not responding');
    throw error;
  }
}

/**
 * Import resume from LinkedIn data export
 */
export async function importResumeFromLinkedIn(
  file: File,
  options?: { language?: string }
): Promise<{ resumeData: ResumeData; warnings?: string[] }> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(isUsingEmulators());

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  const formData = new FormData();
  formData.append('file', file);
  if (options?.language) {
    formData.append('language', options.language);
  }

  const controller = new AbortController();
  const timeoutMs = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
  const timeoutId = setTimeout(() => controller.abort(), Number.isFinite(timeoutMs) ? timeoutMs : 30000);

  try {
    const response = await fetch(`${baseUrl}/importLinkedIn`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: formData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.error || `Failed to import LinkedIn data: ${response.statusText}`);
    }

    const result = await response.json();
    return {
      resumeData: result.resumeData,
      warnings: result.warnings,
    };
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - LinkedIn import service not responding');
    }
    throw error;
  }
}

